import { Column, Index } from 'typeorm';
import type { TradeShowAttendanceRole, TradeShowSightingStatus } from '../../constants/trade-shows';
import { Entity } from '../../decorators';
import type { ImportRowUndoModel } from '../import/import-run.models';
import { BaseEntity } from './base.entity';

/**
 * PD-17636: who is on one of a show's lists, as a link to real records. There is
 * no attendee record: a name on Who's There opens the account, the person or the
 * competitor. A competitor isn't an account, so its people stay as text here.
 */
@Entity('trade_show_attendance')
@Index('idx_trade_show_attendance_show', ['tradeShowId', 'role'])
@Index('idx_trade_show_attendance_client', ['clientId'])
@Index('idx_trade_show_attendance_run', ['importRunId'])
export class TradeShowAttendanceEntity extends BaseEntity {
	@Column('int', { name: 'fk_trade_show__id' })
	public tradeShowId!: number;

	@Column('int', { name: 'editionYear', nullable: true })
	public editionYear?: number | null;

	@Column('int', { name: 'fk_client__id', unsigned: true, nullable: true })
	public clientId?: number | null;

	/** Null for a company-only row. */
	@Column('int', { name: 'fk_contact__id', unsigned: true, nullable: true })
	public contactId?: number | null;

	/** Set for a competitor, which has no account. */
	@Column('int', { name: 'fk_wholesaler__id', unsigned: true, nullable: true })
	public wholesalerId?: number | null;

	@Column('varchar', { name: 'role', length: 20 })
	public role!: TradeShowAttendanceRole;

	/** The tag on the row, like "STAFDA 2026 attendee". */
	@Column('varchar', { name: 'listName', length: 255, nullable: true })
	public listName?: string | null;

	/** Where the list came from: the file's name, the directory, or a person. */
	@Column('varchar', { name: 'source', length: 255, nullable: true })
	public source?: string | null;

	@Column('text', { name: 'note', nullable: true })
	public note?: string | null;

	/** A person at a competitor, who isn't a contact. */
	@Column('varchar', { name: 'personName', length: 255, nullable: true })
	public personName?: string | null;

	@Column('varchar', { name: 'personTitle', length: 255, nullable: true })
	public personTitle?: string | null;

	@Column('varchar', { name: 'booth', length: 60, nullable: true })
	public booth?: string | null;

	/** The import that brought this row in, so the import can be undone. */
	@Column('int', { name: 'fk_import_run__id', nullable: true })
	public importRunId?: number | null;

	/** This row's import created the account, so undoing the import removes it. */
	@Column('boolean', { name: 'createdClient', default: 0 })
	public createdClient!: boolean;

	/** What this row wrote on an account already in Workd, to put back on undo. */
	@Column('json', { name: 'undoData', nullable: true })
	public undoData?: ImportRowUndoModel | null;
}

/**
 * PD-17636: a competitor seen at a show in a year, with where we learned it. The
 * row sits on one edition of a show and the Competitors tab reads every edition
 * of the series together, so a year can be recorded before its edition exists.
 */
@Entity('wholesaler_trade_show')
@Index('idx_wholesaler_trade_show_show', ['tradeShowId'])
@Index('idx_wholesaler_trade_show_competitor', ['wholesalerId'])
export class WholesalerTradeShowEntity extends BaseEntity {
	@Column('int', { name: 'fk_wholesaler__id', unsigned: true })
	public wholesalerId!: number;

	@Column('int', { name: 'fk_trade_show__id' })
	public tradeShowId!: number;

	@Column('int', { name: 'editionYear' })
	public editionYear!: number;

	@Column('varchar', { name: 'status', length: 20, default: 'was_there' })
	public status!: TradeShowSightingStatus;

	@Column('varchar', { name: 'booth', length: 60, nullable: true })
	public booth?: string | null;

	@Column('text', { name: 'note', nullable: true })
	public note?: string | null;

	@Column('varchar', { name: 'sourceUrl', length: 1000, nullable: true })
	public sourceUrl?: string | null;
}
